/**
 * Preferencias de columnas de un listado: qué columnas se ven, en qué orden,
 * con qué ancho, cuáles están fijadas y cuáles recortadas.
 *
 * Única implementación del sistema. La usan los modelos dinámicos y los
 * extractos financieros, que no comparten ni el origen de datos ni el
 * renderizado de celdas pero sí todo este comportamiento.
 *
 * Los nombres coinciden con los que ya usaba `dynamic-model.vue` a propósito:
 * así el host solo declara el mixin y borra sus copias locales.
 *
 * Puntos de extensión (el host los redefine y su versión gana):
 *  - `columnPreferencesViewId`  → clave de persistencia (default: selectedModel)
 *  - `columnPreferencesLayout`  → merges/grupos de columnas (default: {})
 *  - `columnPreferencesHydrate` → completa preferencias al cargarlas
 *  - `columnPreferencesExtras`  → campos extra que se guardan con las preferencias
 *  - `onColumnPreferencesLoaded` → recibe el registro guardado completo
 *  - `persistPinnedColumns`     → dónde se guardan las columnas fijadas
 *  - `onColumnLayoutChanged`    → aviso de cambio de columnas visibles
 *  - `onColumnOrderChanged`     → el usuario reordenó las columnas
 *  - `onColumnWidthChanged`     → el usuario cambió el ancho de una columna
 *  - `listStickyHeaderActive` / `getListStickyHeaderMainTopPx`
 */

import { wencesApi } from "@/api/wencesApi";
import { buildDisplayColumns } from "@/utils/reportColumnLayout";

/** Ancho por defecto de una columna, en px. */
export const DEFAULT_COLUMN_WIDTH_PX = 150;
export const MIN_COLUMN_WIDTH_PX = 50;
export const MAX_COLUMN_WIDTH_PX = 500;

/** Bases de z-index para columnas fijadas (cabecera por encima del cuerpo). */
export const LIST_STICKY_PINNED_HEADER_Z_BASE = 100;
export const LIST_STICKY_PINNED_BODY_Z_BASE = 1;

export default {
  data() {
    return {
      /**
       * Mapa `{ [fieldKey]: boolean }` + metadatos `_columnSums`,
       * `_clipViewMore` (recorta la columna a una línea con «+») y `_wrapText`
       * (parte el texto en renglones). Una columna tiene a lo sumo uno de los dos.
       */
      selectedColumns: {},
      /** Campos visibles, en el orden en que se muestran. */
      visibleTableFields: [],
      savedColumnOrder: [],
      columnWidths: {},
      pinnedColumnKeys: [],
      isLoadingColumnPreferences: false,
      /** Último estado persistido, para detectar cambios sin guardar. */
      savedSelectedColumns: {},
      savedColumnWidths: {},
      savedPinnedColumnKeys: [],
      columnSelectorKey: 0,
      draggedIndex: null,
      isResizing: false,
      resizingColumn: null,
      saveTimeout: null,
    };
  },

  computed: {
    /** Clave de persistencia de las preferencias. */
    columnPreferencesViewId() {
      return this.selectedModel || "";
    },

    /** Merges y grupos de columnas; sin layout es una lista plana. */
    columnPreferencesLayout() {
      return {};
    },

    /** Cabecera fija al desplazar. */
    listStickyHeaderActive() {
      return false;
    },

    /** Columnas a renderizar, ya con merges y en orden. */
    displayTableColumns() {
      return buildDisplayColumns(
        this.visibleTableFields,
        this.columnPreferencesLayout || {}
      );
    },
  },

  beforeDestroy() {
    // Que un guardado con debounce no dispare sobre un componente ya destruido.
    if (this.saveTimeout) {
      clearTimeout(this.saveTimeout);
      this.saveTimeout = null;
    }
  },

  watch: {
    displayTableColumns() {
      this.prunePinnedColumnKeys();
      this.onColumnLayoutChanged();
    },
  },

  methods: {
    /** Gancho para el host: las columnas visibles cambiaron. */
    onColumnLayoutChanged() {},

    /** Gancho para el host: completar preferencias recién cargadas. */
    columnPreferencesHydrate(preferences) {
      return preferences;
    },

    /** Gancho para el host: campos extra que viajan con las preferencias al guardar. */
    columnPreferencesExtras() {
      return {};
    },

    /** Gancho para el host: el registro guardado completo, para leer sus extras. */
    onColumnPreferencesLoaded() {},

    // ---------------------------------------------------------------- carga

    async loadColumnPreferences() {
      this.isLoadingColumnPreferences = true;
      try {
        const viewId = this.columnPreferencesViewId;
        if (!viewId) {
          return {};
        }

        const { data } = await wencesApi.get(
          `/api/users/column-preferences/${viewId}`
        );
        const preferences = data.data.preferences || {};
        const columnOrder = data.data.columnOrder || [];
        const columnWidths = data.data.columnWidths || {};
        const pinnedColumnKeys = Array.isArray(data.data.pinnedColumnKeys)
          ? data.data.pinnedColumnKeys
          : [];

        if (!preferences._columnSums) {
          preferences._columnSums = {};
        }
        if (!preferences._clipViewMore) {
          preferences._clipViewMore = {};
        }
        if (!preferences._wrapText) {
          preferences._wrapText = {};
        }

        this.columnPreferencesHydrate(preferences);
        this.onColumnPreferencesLoaded(data.data);

        // El orden se aplica después, cuando ya existen los campos visibles.
        this.savedColumnOrder = columnOrder;
        this.columnWidths = columnWidths;
        this.pinnedColumnKeys = [...pinnedColumnKeys];

        this.savedSelectedColumns = JSON.parse(JSON.stringify(preferences));
        this.savedColumnWidths = JSON.parse(JSON.stringify(columnWidths));
        this.savedPinnedColumnKeys = [...pinnedColumnKeys];

        return preferences;
      } catch (error) {
        console.error("Error loading column preferences:", error);
        return {};
      } finally {
        this.isLoadingColumnPreferences = false;
      }
    },

    // -------------------------------------------------------------- guardado

    async saveColumnPreferences(immediate = false, applyToAll = false) {
      try {
        const viewId = this.columnPreferencesViewId;
        if (!viewId) {
          return;
        }

        const savePreferences = async () => {
          const columnOrder = this.visibleTableFields.map((field) => field.key);
          const preferences = {
            ...this.columnPreferencesExtras(),
            modelName: viewId,
            preferences: this.selectedColumns,
            columnOrder,
            columnWidths: this.columnWidths,
            pinnedColumnKeys: [...(this.pinnedColumnKeys || [])],
            // Admin o permiso preferences.apply_to_all: aplicar estas preferencias a todos los usuarios.
            applyToAll,
          };

          try {
            await wencesApi.post("/api/users/column-preferences", preferences);
          } catch (error) {
            console.error("Error saving column preferences:", error);
            if (error.response?.data?.error) {
              console.error("Server error:", error.response.data.error);
            }
            throw error;
          }
        };

        if (immediate) {
          await savePreferences();
          return;
        }

        if (this.saveTimeout) {
          clearTimeout(this.saveTimeout);
        }
        this.saveTimeout = setTimeout(savePreferences, 500);
      } catch (error) {
        console.error("Error in saveColumnPreferences:", error);
        throw error;
      }
    },

    // ---------------------------------------------------------------- orden

    /** Reproyecta `display` (con merges) sobre `visibleTableFields`. */
    applyDisplayColumnOrder(display) {
      const newVisible = [];
      const used = new Set();
      display.forEach((col) => {
        if (col.displayType === "merged") {
          col.fields.forEach((f) => {
            if (!used.has(f.key)) {
              newVisible.push(f);
              used.add(f.key);
            }
          });
        } else if (!used.has(col.field.key)) {
          newVisible.push(col.field);
          used.add(col.field.key);
        }
      });
      this.visibleTableFields.forEach((f) => {
        if (!used.has(f.key)) newVisible.push(f);
      });
      this.visibleTableFields = newVisible;
      this.onColumnOrderChanged();
    },

    /** Gancho para el host: el usuario reordenó las columnas. */
    onColumnOrderChanged() {},

    /**
     * Gancho para el host: terminó de arrastrarse el ancho de una columna.
     * Por defecto no persiste nada (dynamic-model usa su botón de guardar).
     */
    onColumnWidthChanged() {},

    reorderDisplayColumnByFieldKey(fieldKey, targetIndex) {
      const display = [...this.displayTableColumns];
      const draggedIndex = display.findIndex(
        (col) => col.displayType === "field" && col.field?.key === fieldKey
      );
      if (draggedIndex < 0) return;
      const index = Math.max(0, Math.min(targetIndex, display.length - 1));
      if (draggedIndex === index) return;
      const [moved] = display.splice(draggedIndex, 1);
      display.splice(index, 0, moved);
      this.applyDisplayColumnOrder(display);
    },

    moveDisplayColumnToStart(fieldKey) {
      this.reorderDisplayColumnByFieldKey(fieldKey, 0);
    },

    moveDisplayColumnToEnd(fieldKey) {
      const len = this.displayTableColumns.length;
      if (len <= 1) return;
      this.reorderDisplayColumnByFieldKey(fieldKey, len - 1);
    },

    // ------------------------------------------------- reordenar arrastrando

    onDragStart(event, index) {
      event.dataTransfer.setData("text/plain", index);
      this.draggedIndex = index;
      event.target.classList.add("dragging");
    },

    onDragOver(event) {
      event.preventDefault();
    },

    onDrop(event, index) {
      event.preventDefault();
      const draggedIndex = parseInt(
        event.dataTransfer.getData("text/plain"),
        10
      );
      const display = [...this.displayTableColumns];
      if (
        Number.isNaN(draggedIndex) ||
        draggedIndex < 0 ||
        draggedIndex >= display.length ||
        index < 0 ||
        index >= display.length
      ) {
        return;
      }
      const [moved] = display.splice(draggedIndex, 1);
      display.splice(index, 0, moved);
      this.applyDisplayColumnOrder(display);

      this.draggedIndex = null;
      event.target.classList.remove("dragging");
    },

    onDragEnter(event) {
      event.preventDefault();
      event.target.classList.add("drag-over");
    },

    onDragLeave(event) {
      event.preventDefault();
      event.target.classList.remove("drag-over");
    },

    // --------------------------------------------------------------- anchos

    getColumnWidth(fieldKey) {
      return this.columnWidths[fieldKey] || DEFAULT_COLUMN_WIDTH_PX;
    },

    listTableColStyle(colKey) {
      const w = this.getColumnWidth(colKey);
      return {
        width: `${w}px`,
        minWidth: `${w}px`,
        maxWidth: `${w}px`,
      };
    },

    startResize(event, fieldKey) {
      event.preventDefault();
      event.stopPropagation();

      this.isResizing = true;
      this.resizingColumn = fieldKey;

      const startX = event.clientX;
      const startWidth = this.getColumnWidth(fieldKey);

      const handleMouseMove = (e) => {
        if (!this.isResizing) return;
        const diff = e.clientX - startX;
        const newWidth = Math.max(
          MIN_COLUMN_WIDTH_PX,
          Math.min(MAX_COLUMN_WIDTH_PX, startWidth + diff)
        );
        this.$set(this.columnWidths, fieldKey, newWidth);
      };

      const handleMouseUp = () => {
        this.isResizing = false;
        this.resizingColumn = null;
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.cursor = "";
        document.body.style.userSelect = "";
        this.onColumnLayoutChanged();
        this.onColumnWidthChanged(fieldKey);
      };

      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
    },

    // ------------------------------------------------------ columnas fijadas

    /** Gancho para el host: persistir las columnas fijadas. */
    async persistPinnedColumns() {
      try {
        await this.saveColumnPreferences(true, false);
        this.savedPinnedColumnKeys = [...(this.pinnedColumnKeys || [])];
      } catch (error) {
        console.error("Error persisting pinned columns:", error);
      }
    },

    isColumnPinned(colKey) {
      return this.pinnedColumnKeys.includes(colKey);
    },

    togglePinColumn(colKey) {
      if (!colKey) return;
      const idx = this.pinnedColumnKeys.indexOf(colKey);
      if (idx >= 0) {
        this.pinnedColumnKeys.splice(idx, 1);
      } else {
        // Se respeta el orden visual, no el orden en que se fueron fijando.
        this.pinnedColumnKeys = (this.displayTableColumns || [])
          .map((c) => c.key)
          .filter((k) => this.pinnedColumnKeys.includes(k) || k === colKey);
      }
      this.persistPinnedColumns();
    },

    unpinColumnKey(colKey) {
      if (!colKey || !this.isColumnPinned(colKey)) return;
      const idx = this.pinnedColumnKeys.indexOf(colKey);
      if (idx >= 0) {
        this.pinnedColumnKeys.splice(idx, 1);
        this.persistPinnedColumns();
      }
    },

    /** Suelta las fijadas que ya no están visibles. */
    prunePinnedColumnKeys() {
      if (!this.pinnedColumnKeys.length) return;
      const cols = this.displayTableColumns || [];
      if (!cols.length || this.isLoadingColumnPreferences) return;
      const visible = new Set(cols.map((c) => c.key));
      const pruned = this.pinnedColumnKeys.filter((k) => visible.has(k));
      if (pruned.length !== this.pinnedColumnKeys.length) {
        this.pinnedColumnKeys = pruned;
        this.persistPinnedColumns();
      }
    },

    /** Offset izquierdo apilando solo columnas fijadas desde el borde. */
    getPinnedDataColumnLeftPx(colIndex) {
      let left = 0;
      const cols = this.displayTableColumns || [];
      for (let i = 0; i < colIndex; i++) {
        const c = cols[i];
        if (c && this.isColumnPinned(c.key)) {
          left += this.getColumnWidth(c.key);
        }
      }
      return left;
    },

    getPinnedColumnStackIndex(colIndex) {
      const cols = this.displayTableColumns || [];
      let stackIndex = 0;
      for (let i = 0; i < colIndex; i++) {
        if (cols[i] && this.isColumnPinned(cols[i].key)) {
          stackIndex += 1;
        }
      }
      return stackIndex;
    },

    /** Desplazamiento superior de la cabecera fija. */
    getListStickyHeaderMainTopPx() {
      return 0;
    },

    listStickyHeaderLeadingThStyle() {
      if (!this.listStickyHeaderActive) return {};
      return {
        position: "sticky",
        top: `${this.getListStickyHeaderMainTopPx()}px`,
        zIndex: 40,
      };
    },

    listDataColStickyStyle(col, colIndex) {
      if (!col || !this.isColumnPinned(col.key)) return {};
      const stackIndex = this.getPinnedColumnStackIndex(colIndex);
      const bodyZBase = this.listStickyHeaderActive
        ? LIST_STICKY_PINNED_BODY_Z_BASE
        : 15;
      return {
        position: "sticky",
        left: `${this.getPinnedDataColumnLeftPx(colIndex)}px`,
        zIndex: bodyZBase + stackIndex,
      };
    },

    listDataColHeaderStickyStyle(col, colIndex) {
      if (!col) return {};
      const mainTop = this.listStickyHeaderActive
        ? this.getListStickyHeaderMainTopPx()
        : null;
      if (this.isColumnPinned(col.key)) {
        const stackIndex = this.getPinnedColumnStackIndex(colIndex);
        const style = {
          position: "sticky",
          left: `${this.getPinnedDataColumnLeftPx(colIndex)}px`,
          zIndex: 35 + stackIndex,
        };
        if (mainTop != null) {
          style.top = `${mainTop}px`;
          style.zIndex = LIST_STICKY_PINNED_HEADER_Z_BASE + stackIndex;
        }
        return style;
      }
      if (mainTop != null) {
        return {
          position: "sticky",
          top: `${mainTop}px`,
          zIndex: 40,
        };
      }
      return {};
    },

    /** Última columna fijada: lleva el borde que separa del área que scrollea. */
    isDataColPinnedBoundary(col, colIndex) {
      if (!col || !this.isColumnPinned(col.key)) return false;
      const cols = this.displayTableColumns || [];
      for (let i = colIndex + 1; i < cols.length; i++) {
        if (this.isColumnPinned(cols[i].key)) return false;
      }
      return true;
    },

    // ------------------------- ancho de columna: recorte «+» o renglones

    isColumnClipViewMoreEnabled(colKey) {
      if (!colKey) return false;
      return this.selectedColumns?._clipViewMore?.[colKey] === true;
    },

    isColumnTextWrapEnabled(colKey) {
      if (!colKey) return false;
      return this.selectedColumns?._wrapText?.[colKey] === true;
    },

    toggleColumnClipViewMore(colKey) {
      this.toggleColumnWidthMark(colKey, "_clipViewMore", "_wrapText");
    },

    toggleColumnTextWrap(colKey) {
      this.toggleColumnWidthMark(colKey, "_wrapText", "_clipViewMore");
    },

    /** Encender una marca apaga la otra: la columna recorta o parte, nunca las dos. */
    toggleColumnWidthMark(colKey, mark, otherMark) {
      if (!colKey) return;
      [mark, otherMark].forEach((key) => {
        if (!this.selectedColumns[key]) this.$set(this.selectedColumns, key, {});
      });
      const next = this.selectedColumns[mark][colKey] !== true;
      this.$set(this.selectedColumns[mark], colKey, next);
      if (next && this.selectedColumns[otherMark][colKey] === true) {
        this.$set(this.selectedColumns[otherMark], colKey, false);
      }
      this.onColumnClipViewMoreChanged();
    },

    /**
     * Formato común de las columnas visibles: `wrap`, `clip` o `none` si todas
     * están igual; `mixed` si no. Sin columnas, `none`.
     */
    allColumnsWidthMode() {
      const keys = (this.displayTableColumns || []).map((col) => col.key);
      const modeOf = (key) => {
        if (this.isColumnTextWrapEnabled(key)) return "wrap";
        if (this.isColumnClipViewMoreEnabled(key)) return "clip";
        return "none";
      };
      const modes = new Set(keys.map(modeOf));
      if (modes.size === 0) return "none";
      return modes.size === 1 ? [...modes][0] : "mixed";
    },

    /**
     * Pone el mismo formato en todas las columnas visibles de una vez (`wrap`,
     * `clip` o `none`). Después cada columna se sigue cambiando por su cuenta.
     */
    setAllColumnsWidthMode(mode) {
      ["_clipViewMore", "_wrapText"].forEach((key) => {
        if (!this.selectedColumns[key]) this.$set(this.selectedColumns, key, {});
      });
      (this.displayTableColumns || []).forEach((col) => {
        if (!col.key) return;
        this.$set(this.selectedColumns._wrapText, col.key, mode === "wrap");
        this.$set(this.selectedColumns._clipViewMore, col.key, mode === "clip");
      });
      this.onColumnClipViewMoreChanged();
    },

    /** Gancho para el host: cambió el recorte o los renglones de una columna. */
    onColumnClipViewMoreChanged() {},
  },
};
